import authApi from '@/services/auth'
import { defineStore } from 'pinia'
import router from '../router'
import { useGlobalStore } from './global'
import client_lookups from '@/assets/json/clientLookups.json'

export const useAuthStore = defineStore('auth', {
  state: () => {
    return {
      accessCode: null,
      state: null,
      user: null,
      profile: null,
      individualInfo: null,
      establishmentInfo: null,
      uaePassToken: null,
      UAEPassLogoutLink: `https://stg-id.uaepass.ae/idshub/logout?redirect_uri=${location.origin}/logout`
    }
  },
  getters: {
    isUAECitizen() {
      return this.user?.applicationId == client_lookups.application.citizen
    },
    isGuestUser() {
      return this.user?.applicationId == client_lookups.application.guest
    },
    isUserTypingCenter() {
      return this.user?.applicationId == client_lookups.typingCenterApplicationId
    },
    isUserEstablishment() {
      return this.user?.applicationId == client_lookups.establishmentApplicationId
    },
    isUserServiceCenter() {
      return this.user?.applicationId == client_lookups.application.serviceCenters
    },
    // Even guest users have it own data, after request guest/login api
    isLoggedIn() {
      return this.user && !this.isGuestUser
    },
    isFreezone() {
      return [3, 5].includes(this.user.accountTypeId)
    },
    /**
     * primarily used in the display of headers, this getter checks if
     * a user is a public user or not
     */
    isPublic() {
      if (this.isGuestUser) return true
      const { resident, citizen, TypingCenter, establishment, iCAEstablishmet, gcc_citizen } =
        client_lookups.application

      const nonPublicAppIds = [
        resident,
        citizen,
        TypingCenter,
        establishment,
        iCAEstablishmet,
        gcc_citizen
      ]

      return nonPublicAppIds.includes(this.user.applicationId) === false
    },
    profileName() {
      const globalStore = useGlobalStore()
      if (!this.isLoggedIn) return

      if (globalStore.languageId == 1) {
        const { arabicFirstName, arabicFamilyName, fullnameAR, firstnameAR, lastnameAR } = this.user
        if (lastnameAR) {
          return fullnameAR ?? `${firstnameAR} ${lastnameAR}`
        }
        return arabicFirstName ? `${arabicFirstName} ${arabicFamilyName}` : fullnameAR
      }

      const { englishFirstName, englishFamilyName, fullnameEN, firstnameEN, lastnameEN } = this.user
      if (lastnameEN) {
        return fullnameEN ?? `${firstnameEN} ${lastnameEN}`
      }
      return englishFirstName ? `${englishFirstName} ${englishFamilyName}` : fullnameEN
    },
    /**
     * used in dashboard
     */
    searchCategories() {
      return [
        { name: 'mainApplicationNoteTitle', id: '1' },
        { name: 'IndividualSponsporedResi', id: '2' },
        { name: 'myRequests', id: '3' },
        { name: 'familyMembers', id: '4' },
        { name: 'domesticHelpers', id: '5' },
        { name: 'work', id: '6' }
      ]
    },
    availableCategoriesPerApplicationId() {
      if (!this.user) return
      switch (this.user.applicationId) {
        case 1:
        case 2:
        case 21:
          return this.searchCategories.filter((c) => [1, 3].includes(+c.id))
        case 6:
          return this.searchCategories
        case 7:
          return this.searchCategories.filter((c) => [1, 2, 3, 5, 6].includes(+c.id))
        default:
          return this.searchCategories.filter((c) => [1, 2, 3, 5].includes(+c.id))
      }
    }
  },
  actions: {
    async getAccessToken() {
      try {
        const response = await authApi.getAccessToken({
          params: {
            grant_type: 'authorization_code',
            redirect_uri: `${location.origin}/auth/pass`,
            code: this.accessCode
          },
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Basic c2FuZGJveF9zdGFnZTpzYW5kYm94X3N0YWdl`
          }
        })
        this.uaePassToken = response
      } catch (error) {
        return error
      }
    },
    async getUAEPassUserInfo() {
      try {
        if (!this.uaePassToken.access_token) {
          return
        }
        const response = await authApi.getUAEPassUserInfo({
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${this.uaePassToken.access_token}`
          }
        })
        this.user = response
        this.parseClaims()
        router.push('/individual')
      } catch (error) {
        return error
      }
    },
    parseClaims() {
      const { userClaims } = this.user

      const obj = {}

      /**
       * first, convert the array of objects into an object
       */
      userClaims.forEach(({ key, values }) => {
        obj[key] = values
      })

      /**
       * second, parse the values
       */
      Object.entries(obj).forEach(([key, [value]]) => {
        try {
          obj[key] = JSON.parse(value.toLowerCase())
        } catch (e) {
          obj[key] = value
        }
      })

      this.user.userClaimsObj = obj
    },
    UAEPassLogout() {
      this.user = null
      this.uaePassToken = null
    },
    async emailLogin({ email, password }) {
      this.user = null
      this.user = await authApi.emailLogin({
        userName: email,
        password
      })
      this.parseClaims()
    },
    async logout() {
      this.$reset()
      localStorage.clear()
      sessionStorage.clear()
      // window.location.reload()
    },

    async guestLogin() {
      this.user = await authApi.guestLogin()
    },
    async getProfile() {
      this.profile = await authApi.getProfile()
    },

    // registration methods
    async registerUser(type, payload) {
      await authApi.registerUser(type, payload)
    },

    async fetchEstablishmentInfo(params) {
      this.establishmentInfo = await authApi.getEstablishmentInfo(params)
    },
    async fetchIndividualInfo() {
      this.individualInfo = await authApi.getIndividualInfo()
    }
  },
  persist: true
})
